/**
 * RBAC domain types — mirror the BE DTOs 1:1.
 *
 * The backend serializes with SNAKE_CASE (Jackson PropertyNamingStrategy), so
 * every field coming off the wire is snake_case. We keep the over-the-wire
 * shape here verbatim and let callers map to camelCase at the view layer if
 * they want — this avoids a hidden transformer that can go out of sync.
 */

/** Atomic permission: one action on one screen. */
export interface Permission {
  permission_id: number
  screen_id: number
  action: string
  /** Format: `<SCREEN_CODE>_<ACTION>`, e.g. `USERS_VIEW`. Never colon-separated. */
  code: string
  description?: string | null
}

/** A screen (module) catalog entry with its atomic permissions. */
export interface Screen {
  screen_id: number
  code: string
  name: string
  module?: string | null
  description?: string | null
  sort_order: number
  is_active: boolean
  permissions: Permission[]
}

/** A permission group (role template) created by SuperAdmin via the UI. */
export interface Group {
  group_id: number
  code: string
  name: string
  description?: string | null
  /** True for the seeded `SUPER_ADMIN` group — UI must block edit/delete. */
  is_system: boolean
  permission_ids: number[]
  permission_codes: string[]
  member_count: number
  created_at?: string
  updated_at?: string
}

export interface GroupCreateRequest {
  code: string
  name: string
  description?: string
  permission_ids: number[]
}

export interface GroupUpdateRequest {
  name: string
  description?: string
  /**
   * Optional. When omitted (or undefined) the BE leaves the existing
   * permission set untouched. When sent (even as `[]`) the BE replaces the
   * permission set atomically inside the same transaction as the metadata
   * update — this is the path the edit form uses to avoid the partial-save
   * race we hit when the FE issued two separate requests.
   */
  permission_ids?: number[]
}

/** POST /admin/rbac/users/{adminId}/groups body. */
export interface AssignGroupsRequest {
  group_ids: number[]
}

/** Response of GET /admin/rbac/users/{adminId}/permissions and embedded in AuthResponse. */
export interface EffectivePermissions {
  admin_id: number
  is_super_admin: boolean
  /** Single-entry `['*']` for super-admins; otherwise a UPPER_SNAKE list. */
  permissions: string[]
  groups: Group[]
}

/** Wildcard marker returned by BE when the caller is SUPER_ADMIN. */
export const SUPER_ADMIN_WILDCARD = '*' as const
